/**
 * DemoPage - Read-only product preview
 * 
 * Features:
 * - Sample data only (incidents, hazards, inspections, actions)
 * - No write operations
 * - No account required
 * - Clear demo banner
 * - Purchase CTA
 * 
 * Route: /demo
 */
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  AlertTriangle, FileText, ClipboardCheck, CheckCircle,
  ArrowRight, Eye, X, ChevronRight, Calendar, MapPin, User
} from 'lucide-react';
import './DemoPage.css';

// Sample data - realistic but clearly demo
const SAMPLE_INCIDENTS = [
  {
    id: 'demo-inc-001',
    type: 'Near Miss',
    title: 'Forklift near miss in warehouse',
    site: 'Main Warehouse',
    date: '2024-01-15',
    status: 'CLOSED',
    severity: 'MEDIUM',
    description: 'Forklift operator narrowly avoided collision with pedestrian near loading bay.'
  },
  {
    id: 'demo-inc-002',
    type: 'Incident',
    title: 'Minor slip on wet floor',
    site: 'Head Office',
    date: '2024-01-22',
    status: 'IN_TREATMENT',
    severity: 'LOW',
    description: 'Employee slipped on wet floor in kitchen area. No injury reported.'
  },
  {
    id: 'demo-inc-003',
    type: 'Hazard',
    title: 'Exposed electrical wiring',
    site: 'Workshop B',
    date: '2024-02-01',
    status: 'TRIAGED',
    severity: 'HIGH',
    description: 'Damaged cable conduit exposing electrical wiring near workbench.'
  }
];

const SAMPLE_HAZARDS = [
  {
    id: 'demo-haz-001',
    title: 'Uneven flooring in entry',
    site: 'Main Office',
    status: 'OPEN',
    risk: 'MEDIUM',
    reported: '2024-01-10'
  },
  {
    id: 'demo-haz-002',
    title: 'Missing handrail on stairs',
    site: 'Warehouse',
    status: 'IN_PROGRESS',
    risk: 'HIGH',
    reported: '2024-01-18'
  },
  {
    id: 'demo-haz-003',
    title: 'Poor lighting in car park',
    site: 'Head Office',
    status: 'CLOSED',
    risk: 'LOW',
    reported: '2024-01-05'
  }
];

const SAMPLE_ACTIONS = [
  {
    id: 'demo-act-001',
    title: 'Install warning signs at loading bay',
    assignee: 'John Smith',
    due: '2024-02-15',
    status: 'DONE',
    priority: 'HIGH'
  },
  {
    id: 'demo-act-002',
    title: 'Repair floor tiles in kitchen',
    assignee: 'Sarah Jones',
    due: '2024-02-20',
    status: 'IN_PROGRESS',
    priority: 'MEDIUM'
  },
  {
    id: 'demo-act-003',
    title: 'Arrange electrical inspection',
    assignee: 'Mike Wilson',
    due: '2024-02-10',
    status: 'OVERDUE',
    priority: 'HIGH'
  }
];

const SAMPLE_INSPECTIONS = [
  {
    id: 'demo-insp-001',
    name: 'Monthly Fire Safety Check',
    site: 'All Sites',
    lastCompleted: '2024-01-28',
    nextDue: '2024-02-28',
    status: 'SCHEDULED'
  },
  {
    id: 'demo-insp-002',
    name: 'Forklift Pre-Start Checklist',
    site: 'Warehouse',
    lastCompleted: '2024-02-01',
    nextDue: '2024-02-02',
    status: 'DUE'
  }
];

const StatusBadge = ({ status }) => {
  const statusStyles = {
    'OPEN': { bg: '#fef3c7', color: '#92400e' },
    'TRIAGED': { bg: '#dbeafe', color: '#1e40af' },
    'IN_TREATMENT': { bg: '#e0e7ff', color: '#3730a3' },
    'IN_PROGRESS': { bg: '#e0e7ff', color: '#3730a3' },
    'CLOSED': { bg: '#d1fae5', color: '#065f46' },
    'DONE': { bg: '#d1fae5', color: '#065f46' },
    'OVERDUE': { bg: '#fee2e2', color: '#991b1b' },
    'SCHEDULED': { bg: '#f3f4f6', color: '#374151' },
    'DUE': { bg: '#fef3c7', color: '#92400e' },
  };
  const style = statusStyles[status] || statusStyles['OPEN'];
  
  return (
    <span 
      className="demo-status-badge"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {status.replace('_', ' ')}
    </span>
  );
};

const SeverityBadge = ({ severity }) => {
  const severityStyles = {
    'LOW': { bg: '#d1fae5', color: '#065f46' },
    'MEDIUM': { bg: '#fef3c7', color: '#92400e' },
    'HIGH': { bg: '#fee2e2', color: '#991b1b' },
  };
  const style = severityStyles[severity] || severityStyles['LOW'];
  
  return (
    <span 
      className="demo-severity-badge"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {severity}
    </span>
  );
};

const DemoPage = ({ onPurchase, onExit }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedItem, setSelectedItem] = useState(null);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Eye },
    { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
    { id: 'hazards', label: 'Hazards', icon: FileText },
    { id: 'actions', label: 'Actions', icon: CheckCircle },
    { id: 'inspections', label: 'Inspections', icon: ClipboardCheck },
  ];

  const renderDashboard = () => (
    <div className="demo-dashboard">
      <div className="demo-stats-grid">
        <Card className="demo-stat-card">
          <div className="demo-stat-value">3</div>
          <div className="demo-stat-label">Open Incidents</div>
        </Card>
        <Card className="demo-stat-card demo-stat-warning">
          <div className="demo-stat-value">2</div>
          <div className="demo-stat-label">Active Hazards</div>
        </Card>
        <Card className="demo-stat-card demo-stat-danger">
          <div className="demo-stat-value">1</div>
          <div className="demo-stat-label">Overdue Actions</div>
        </Card>
        <Card className="demo-stat-card demo-stat-success">
          <div className="demo-stat-value">5</div>
          <div className="demo-stat-label">Closed This Month</div>
        </Card>
      </div>

      <div className="demo-recent-grid">
        <Card className="demo-recent-card">
          <h3>Recent Incidents</h3>
          <div className="demo-list">
            {SAMPLE_INCIDENTS.slice(0, 2).map(inc => (
              <div key={inc.id} className="demo-list-item" onClick={() => setSelectedItem(inc)}>
                <div className="demo-list-item-content">
                  <span className="demo-list-item-title">{inc.title}</span>
                  <span className="demo-list-item-meta">{inc.site} • {inc.date}</span>
                </div>
                <StatusBadge status={inc.status} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="demo-recent-card">
          <h3>Pending Actions</h3>
          <div className="demo-list">
            {SAMPLE_ACTIONS.filter(a => a.status !== 'DONE').map(action => (
              <div key={action.id} className="demo-list-item" onClick={() => setSelectedItem(action)}>
                <div className="demo-list-item-content">
                  <span className="demo-list-item-title">{action.title}</span>
                  <span className="demo-list-item-meta">Due: {action.due}</span>
                </div>
                <StatusBadge status={action.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  const renderIncidents = () => (
    <div className="demo-list-page">
      <div className="demo-list-header">
        <h2>Incidents & Reports</h2>
        <Button disabled className="demo-disabled-btn">+ New Report</Button>
      </div>
      <div className="demo-cards-list">
        {SAMPLE_INCIDENTS.map(inc => (
          <Card key={inc.id} className="demo-record-card" onClick={() => setSelectedItem(inc)}>
            <div className="demo-record-header">
              <span className="demo-record-type">{inc.type}</span>
              <SeverityBadge severity={inc.severity} />
            </div>
            <h4>{inc.title}</h4>
            <p className="demo-record-desc">{inc.description}</p>
            <div className="demo-record-footer">
              <span><MapPin size={14} /> {inc.site}</span>
              <span><Calendar size={14} /> {inc.date}</span>
              <StatusBadge status={inc.status} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderHazards = () => (
    <div className="demo-list-page">
      <div className="demo-list-header">
        <h2>Hazard Register</h2>
        <Button disabled className="demo-disabled-btn">+ Report Hazard</Button>
      </div>
      <div className="demo-cards-list">
        {SAMPLE_HAZARDS.map(haz => (
          <Card key={haz.id} className="demo-record-card" onClick={() => setSelectedItem(haz)}>
            <div className="demo-record-header">
              <SeverityBadge severity={haz.risk} />
              <StatusBadge status={haz.status} />
            </div>
            <h4>{haz.title}</h4>
            <div className="demo-record-footer">
              <span><MapPin size={14} /> {haz.site}</span>
              <span><Calendar size={14} /> {haz.reported}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="demo-list-page">
      <div className="demo-list-header">
        <h2>Corrective Actions</h2>
        <Button disabled className="demo-disabled-btn">+ New Action</Button>
      </div>
      <div className="demo-cards-list">
        {SAMPLE_ACTIONS.map(action => (
          <Card key={action.id} className={`demo-record-card ${action.status === 'OVERDUE' ? 'demo-overdue' : ''}`}>
            <div className="demo-record-header">
              <span className={`demo-priority demo-priority-${action.priority.toLowerCase()}`}>
                {action.priority}
              </span>
              <StatusBadge status={action.status} />
            </div>
            <h4>{action.title}</h4>
            <div className="demo-record-footer">
              <span><User size={14} /> {action.assignee}</span>
              <span><Calendar size={14} /> Due: {action.due}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderInspections = () => (
    <div className="demo-list-page">
      <div className="demo-list-header">
        <h2>Inspections</h2>
        <Button disabled className="demo-disabled-btn">+ New Inspection</Button>
      </div>
      <div className="demo-cards-list">
        {SAMPLE_INSPECTIONS.map(insp => (
          <Card key={insp.id} className="demo-record-card">
            <div className="demo-record-header">
              <StatusBadge status={insp.status} />
            </div>
            <h4>{insp.name}</h4>
            <div className="demo-record-footer">
              <span><MapPin size={14} /> {insp.site}</span>
              <span>Last: {insp.lastCompleted}</span>
              <span>Next: {insp.nextDue}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'incidents': return renderIncidents();
      case 'hazards': return renderHazards();
      case 'actions': return renderActions();
      case 'inspections': return renderInspections();
      default: return renderDashboard();
    }
  };

  return (
    <div className="demo-page" data-testid="demo-page">
      {/* Demo Banner */}
      <div className="demo-banner" data-testid="demo-banner">
        <div className="demo-banner-content">
          <Eye size={18} />
          <span><strong>Demo Mode</strong> — Sample data only. Read-only preview.</span>
        </div>
        <div className="demo-banner-actions">
          <Button size="sm" onClick={onPurchase} data-testid="demo-purchase-btn">
            Purchase RISKEE — $99 AUD
            <ArrowRight size={16} />
          </Button>
          <button className="demo-exit-btn" onClick={onExit} aria-label="Exit demo">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="demo-header">
        <div className="demo-header-content">
          <div className="demo-logo">
            <img src="/riskee_logo.png" alt="RISKEE" className="demo-logo-img" />
            <span className="demo-logo-text">RISKEE</span>
            <span className="demo-badge">DEMO</span>
          </div>
          <nav className="demo-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`demo-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`demo-nav-${tab.id}`}
              >
                <tab.icon size={18} strokeWidth={1.5} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="demo-main">
        <div className="demo-content">
          {renderContent()}
        </div>
      </main>

      {/* Footer CTA */}
      <footer className="demo-footer">
        <div className="demo-footer-content">
          <p>Ready to manage your safety records?</p>
          <Button onClick={onPurchase} data-testid="demo-footer-purchase-btn">
            Purchase RISKEE — $99 AUD
            <ArrowRight size={18} />
          </Button>
        </div>
      </footer>

      {/* Item Detail Modal (read-only) */}
      {selectedItem && (
        <div className="demo-modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="demo-modal" onClick={e => e.stopPropagation()}>
            <button className="demo-modal-close" onClick={() => setSelectedItem(null)}>
              <X size={20} />
            </button>
            <h3>{selectedItem.title}</h3>
            {selectedItem.description && <p>{selectedItem.description}</p>}
            <div className="demo-modal-meta">
              {selectedItem.site && <span><MapPin size={14} /> {selectedItem.site}</span>}
              {selectedItem.date && <span><Calendar size={14} /> {selectedItem.date}</span>}
              {selectedItem.assignee && <span><User size={14} /> {selectedItem.assignee}</span>}
            </div>
            <div className="demo-modal-notice">
              <Eye size={16} />
              <span>This is sample data for demonstration purposes.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoPage;
